/**
 * Client-side feature-access gating.
 *
 * The server stamps the EFFECTIVE (org + user merged) access onto the user
 * object returned by login / GET /api/users/:id, so here we simply read
 * `user.featureAccess`. See server/utils/featureAccess.js for the merge logic.
 *
 * Usage:
 *   import { isAiEnabled, isAiFeatureEnabled, filterSidebarNavItems } from '../utils/featureAccess';
 *
 *   if (isAiFeatureEnabled(user, 'listing_description')) { ...render AI button... }
 *   const navItems = filterSidebarNavItems(user, getSidebarNavItems({ ... }));
 */

import { isTysonOrg } from './isTysonOrg';
import { isApexOrg } from './isApexOrg';
import { isMembershipExperience, isPartnerRole } from './isMembershipExperience';
import { AI_FEATURE_KEYS } from '../config/featureCatalog';
import { isStudioReportsOnly } from './studioAccess';

/**
 * Nav item ids always hidden for every Tyson user type. Tyson does not use
 * the FICA RCR surface. Admin hide-nav flags sit on top of this list.
 */
const TYSON_HIDDEN_NAV_IDS = ['compliance-rcr'];

/** Membership Network self-signup: matching + the in-app how-to assistant. */
const MEMBERSHIP_ALLOWED_AI = new Set(['lead_matching', 'assistant']);

const EMPTY = { hideAllAI: false, hiddenAiFeatures: [], hiddenNavIds: [], revealedNavIds: [], hideAutomation: false, hideProspecting: false, hideInternationalListings: false, hideStudio: false, hideStudioReports: false, hideStudioTemplates: false, hideStudioForms: false, hideStudioSocial: false };

/** Read the current user from localStorage (falls back to null). */
export function getStoredUser() {
    try {
        return JSON.parse(localStorage.getItem('user'));
    } catch (_) {
        return null;
    }
}

/** Normalized feature-access object for a user (never throws). */
export function getFeatureAccess(user) {
    const fa = (user && user.featureAccess) || EMPTY;
    return {
        hideAllAI: !!fa.hideAllAI,
        hiddenAiFeatures: Array.isArray(fa.hiddenAiFeatures) ? fa.hiddenAiFeatures : [],
        hiddenNavIds: Array.isArray(fa.hiddenNavIds) ? fa.hiddenNavIds : [],
        revealedNavIds: Array.isArray(fa.revealedNavIds) ? fa.revealedNavIds : [],
        hideAutomation: !!fa.hideAutomation,
        hideProspecting: !!fa.hideProspecting,
        hideInternationalListings: !!fa.hideInternationalListings,
        hideStudio: !!fa.hideStudio,
        hideStudioReports: !!fa.hideStudioReports,
        hideStudioTemplates: !!fa.hideStudioTemplates,
        hideStudioForms: !!fa.hideStudioForms,
        hideStudioSocial: !!fa.hideStudioSocial,
        studioReportsOnly: isStudioReportsOnly(user),
    };
}

/** True when the "International Listing Details" section should be hidden for this user. */
export function isInternationalListingsHidden(user) {
    return getFeatureAccess(user).hideInternationalListings;
}

/** True when IPM Studio (the whole /my-reports workspace) should be hidden. */
export function isStudioHidden(user) {
    if (isTysonOrg(user)) return false;
    return getFeatureAccess(user).hideStudio;
}

/** True when the Studio "My Reports" tab should be hidden (or all of Studio is off). */
export function isStudioReportsHidden(user) {
    if (isTysonOrg(user)) return false;
    const fa = getFeatureAccess(user);
    return fa.hideStudio || fa.hideStudioReports;
}

/** True when the Studio "My Templates" tab should be hidden (or all of Studio is off). */
export function isStudioTemplatesHidden(user) {
    if (isStudioReportsOnly(user)) return true;
    const fa = getFeatureAccess(user);
    return fa.hideStudio || fa.hideStudioTemplates;
}

/**
 * True when this user may author Template Studio brochures/letters.
 * Platform admins + agency-level admins (enterprise, agency, branch_manager),
 * matching the document-template authoring roles. Hidden if the whole Studio is off.
 * Reports-only contracts exclude the separate Template Studio editor.
 */
const TEMPLATE_AUTHOR_ROLES = ['admin', 'enterprise', 'partner_enterprise', 'agency', 'branch_manager'];
export function canAuthorPortalTemplates(user) {
    if (isStudioReportsOnly(user)) return false;
    if (getFeatureAccess(user).hideStudio) return false;
    if (isApexOrg(user)) return false;
    const role = String(user?.role || '').toLowerCase();
    return TEMPLATE_AUTHOR_ROLES.includes(role);
}

/** True when the Studio "My Forms" tab should be hidden (or all of Studio is off). */
export function isStudioFormsHidden(user) {
    if (isStudioReportsOnly(user)) return true;
    const fa = getFeatureAccess(user);
    return fa.hideStudio || fa.hideStudioForms;
}

/** True when the Studio "Social Templates" tab should be hidden (or all of Studio is off). */
export function isStudioSocialHidden(user) {
    if (isStudioReportsOnly(user)) return true;
    const fa = getFeatureAccess(user);
    return fa.hideStudio || fa.hideStudioSocial;
}

/** Apply the same Studio contract to tabs and directly opened editors. */
export function isStudioTabHidden(user, tab) {
    if (isStudioReportsOnly(user) && tab !== 'reports') return true;
    switch (tab) {
        case 'reports':
        case 'brochures':
        case 'documents':
        case 'invoices': return isStudioReportsHidden(user);
        case 'templates': return isStudioTemplatesHidden(user);
        case 'forms': return isStudioFormsHidden(user);
        case 'social':
        case 'whatsapp': return isStudioSocialHidden(user);
        case 'templateStudio': return !canAuthorPortalTemplates(user);
        case 'automations': return isStudioHidden(user) || isAutomationHidden(user);
        default: return true;
    }
}

const PAID_PARTNER_WORKSPACE_PLANS = new Set([
    'premium',
    'custom',
    'enterprise',
    'partner enterprise',
    'partner_enterprise',
]);

/** Paid partner plans include mailbox sync, calendar, and pipeline automation. */
export function partnerHasWorkspaceAddons(user) {
    if (!user || !isPartnerRole(user)) return true;
    const plan = String(user.subscriptionPlan || '').trim().toLowerCase();
    if (!plan || plan === 'free' || plan === 'basic') return false;
    return PAID_PARTNER_WORKSPACE_PLANS.has(plan);
}

/** Free / Basic industry partners must not link mail, calendar, or automation. */
export function isPartnerWorkspaceAddonsLocked(user) {
    return isPartnerRole(user) && !partnerHasWorkspaceAddons(user);
}

/** True when pipeline automation-rule sections should be hidden for this user. */
export function isAutomationHidden(user) {
    if (isMembershipExperience(user)) return true;
    if (isPartnerWorkspaceAddonsLocked(user)) return true;
    // Donna: every Tyson person (office + practitioners) keeps the rules editor.
    if (isTysonOrg(user)) return false;
    // Other agencies: linked agents follow the office process; independent
    // agents and agency/enterprise owners keep the editor.
    const role = String(user?.role || '').toLowerCase();
    if (role === 'agency_agent') return true;
    return getFeatureAccess(user).hideAutomation;
}

/**
 * Office / brand admins may edit org brand colours, sender identity, and the
 * company email signature. Linked practitioners may use those assets, not edit them.
 */
const OFFICE_BRAND_ROLES = ['admin', 'enterprise', 'partner_enterprise', 'agency', 'branch_manager'];
export function canManageOfficeBrand(user) {
    const role = String(user?.role || '').toLowerCase();
    if (OFFICE_BRAND_ROLES.includes(role)) return true;
    return role === 'agency_agent' && /\badministrator\b/i.test(String(user?.title || ''));
}

/** True when the Prospecting tool (tab + /prospecting route) should be hidden. */
export function isProspectingHidden(user) {
    if (isMembershipExperience(user)) return true;
    return getFeatureAccess(user).hideProspecting;
}

/** True when Development Inventory (tab + /inventory) should be hidden. */
export function isInventoryHidden(user) {
    if (isMembershipExperience(user)) return true;
    return false;
}

/**
 * Roles that get the Mass Marketing surface (now a tab under Marketing).
 */
const MASS_MARKETING_ROLES = ['agency', 'independent_agent', 'agency_agent', 'enterprise', 'partner_enterprise'];

/** True when the Mass Marketing tab (under Marketing) should be hidden. */
export function isMassMarketingHidden(user) {
    const role = (user?.role || '').toLowerCase();
    if (!MASS_MARKETING_ROLES.includes(role)) return true;
    return getFeatureAccess(user).hiddenNavIds.includes('mass-marketing');
}

/** True when ANY AI feature is allowed for this user (i.e. AI is not fully off). */
export function isAiEnabled(user) {
    if (isMembershipExperience(user)) return true; // matching remains
    return !getFeatureAccess(user).hideAllAI;
}

/**
 * True when a specific AI feature (by key from featureCatalog.js) is enabled.
 * The master `hideAllAI` switch overrides individual keys.
 * Membership Network self-signup: `lead_matching` and `assistant` stay on.
 */
export function isAiFeatureEnabled(user, featureKey) {
    if (isMembershipExperience(user)) {
        return MEMBERSHIP_ALLOWED_AI.has(featureKey);
    }
    // Every Tyson login keeps the in-app assistant (Chat with Tyson Properties),
    // including Administrators. Do not hide it for agency_agent or org flags.
    if (featureKey === 'assistant' && isTysonOrg(user)) return true;
    const fa = getFeatureAccess(user);
    if (fa.hideAllAI) return false;
    if (!featureKey) return true;
    return !fa.hiddenAiFeatures.includes(featureKey);
}

/**
 * Listing create/edit AI (Quick start, voice draft, photo analysis, copy,
 * address enrichment). Matching is intentionally separate.
 */
export function isListingAiAssistEnabled(user) {
    return isAiFeatureEnabled(user, 'voice_listing')
        || isAiFeatureEnabled(user, 'photo_ai')
        || isAiFeatureEnabled(user, 'listing_description');
}

/** Feature-access payload stamped onto Membership Network self-signups. */
export function membershipFeatureAccessDefaults() {
    return {
        hideAllAI: false,
        hideProspecting: true,
        hiddenAiFeatures: AI_FEATURE_KEYS.filter((k) => !MEMBERSHIP_ALLOWED_AI.has(k)),
        hiddenNavIds: [],
        hideAutomation: true,
        hideInternationalListings: false,
        hideStudio: false,
        hideStudioReports: false,
        hideStudioTemplates: false,
        hideStudioForms: false,
        hideStudioSocial: false,
    };
}

/**
 * Effective hidden sidebar ids for this user: own + org flags, Studio
 * kill-switch, and Tyson’s always-off FICA item.
 */
export function effectiveHiddenNavIds(user) {
    const fa = getFeatureAccess(user);
    const hidden = new Set(fa.hiddenNavIds);
    if (fa.hideStudio) hidden.add('my-reports');
    if (isTysonOrg(user)) {
        TYSON_HIDDEN_NAV_IDS.forEach((id) => hidden.add(id));
        // HQ sometimes copies a "hide Rentals" flag onto the office. Rentals
        // still opens the rental suite for Tyson.
        hidden.delete('rentals');
        // Every Tyson practitioner uses the office Sales pipeline (deals, offers,
        // stage gates). Do not inherit an HQ "hide Sales Suite" onto agents.
        hidden.delete('revenue');
    }
    (fa.revealedNavIds || []).forEach((id) => hidden.delete(id));
    return hidden;
}

/** True when a sidebar nav item (by id) should be hidden for this user. */
export function isNavHidden(user, navId) {
    if (!navId) return false;
    return effectiveHiddenNavIds(user).has(navId);
}

/**
 * True when Rental Suite (nav + dashboard Rentals context) should be off.
 * Tyson keeps the suite even if HQ hid it.
 */
export function isRentalsHidden(user) {
    if (isTysonOrg(user)) return false;
    return isNavHidden(user, 'rentals');
}

/** True when Sales Suite (nav + dashboard Sales context) should be off. */
export function isSalesHidden(user) {
    if (isTysonOrg(user)) return false;
    return isNavHidden(user, 'revenue');
}

/** Both / Sales / Rentals pills on Adash homes. Hidden suites are omitted. */
export function salesRentalsSegOptions(user) {
    const hideSales = isSalesHidden(user);
    const hideRentals = isRentalsHidden(user);
    const opts = [];
    if (!hideSales && !hideRentals) opts.push({ value: 'both', label: 'Both' });
    if (!hideSales) opts.push({ value: 'sales', label: 'Sales' });
    if (!hideRentals) opts.push({ value: 'rentals', label: 'Rentals' });
    return opts;
}

export function clampSalesRentalsContext(context, user) {
    const opts = salesRentalsSegOptions(user);
    if (opts.some((o) => o.value === context)) return context;
    return opts[0]?.value || 'sales';
}

/**
 * Roles that are "agents" reporting to a manager/agency. Their vault is
 * view-only - they can open documents but not download or delete them.
 * The managing roles (agency owner, branch manager, enterprise, admin) and
 * self-managed independent agents keep full rights.
 */
const VAULT_READ_ONLY_ROLES = ['agent', 'agency_agent'];

/**
 * True when the vault should be view-only for this user: documents can be
 * opened/viewed but the download and delete actions are hidden. Reserved for
 * admins/managers (see VAULT_READ_ONLY_ROLES).
 */
export function isVaultReadOnly(user) {
    const role = String(user?.role || '').toLowerCase();
    return VAULT_READ_ONLY_ROLES.includes(role);
}

/** Filter a sidebar nav item list down to the ones this user may see. */
export function filterSidebarNavItems(user, items) {
    if (!Array.isArray(items)) return [];
    const hidden = effectiveHiddenNavIds(user);
    if (hidden.size === 0) return items;
    return items.filter((it) => !it || !hidden.has(it.id));
}
